/**
 * Bundle Checkout — shared "Proceed to Checkout" logic.
 *
 * Single source of truth used by:
 *   - bundle-hero-block (popup CTA)
 *   - bundle-bundle-offer-block (product card CTAs)
 *
 * Exposes:
 *   window.handleBundleProceedToCheckout(variantId, options) -> Promise
 *
 * options = {
 *   button?:       HTMLButtonElement       // disabled + .is-loading + aria-busy applied while running
 *   statusEl?:     HTMLElement             // visually-hidden live region for screen readers
 *   errorEl?:      HTMLElement             // element used to display the error message
 *   loadingLabel?: string                  // text written to statusEl while loading
 *   errorLabel?:   string                  // fallback text when the server doesn't return a message
 *   discount?:     string                  // discount code appended to the checkout URL (default 'welcome')
 *   quantity?:     number                  // line item quantity (default 1)
 * }
 *
 * On success the browser is redirected to /checkout?discount=<discount>.
 * On failure the UI is restored and the Promise rejects with the error.
 *
 *
 * Why this file is more complicated than it looks: iOS Safari
 * -----------------------------------------------------------
 * Handing the customer to checkout leaves this page frozen in Safari's
 * back/forward cache with its button still spinning — deliberately, since we
 * expect never to see the page again. Pressing Back gives that frozen copy
 * straight back: same DOM, same JavaScript, no scripts re-run. Two things are
 * then broken.
 *
 *   1. The spinner is still turning and the button is still disabled.
 *   2. Worse, the restored page's network connections are dead. A fresh
 *      cart/add.js request hangs forever rather than failing, so the button
 *      spins again and the customer never reaches checkout.
 *
 * Only a genuine reload fixes (2), and a reload happens to fix (1) for free —
 * along with the stale cart count and prices the frozen copy is also showing.
 * So the whole strategy is: notice the customer came back from checkout, and
 * reload.
 *
 * Noticing is the hard part, because Safari cannot be relied on to say so.
 * It is detected two independent ways, and neither is required to work:
 *
 *   - pageshow with event.persisted, the purpose-built signal, when it fires.
 *   - a wall-clock gap in the heartbeat below. Timers are suspended while a
 *     page is frozen, so a one-second tick that arrives ten seconds late is
 *     itself proof the page was cached and resumed. This needs no cooperation
 *     from the browser at all.
 *
 * Both are gated on leftForCheckout so that ordinary app-switching — which
 * also freezes timers — never triggers a surprise reload.
 */
(function () {
  if (window.handleBundleProceedToCheckout) return;

  var DEFAULT_DISCOUNT = 'welcome';
  var DEFAULT_ERROR = 'Sorry, something went wrong adding this item to the cart. Please try again.';

  var HEARTBEAT_MS = 1000;
  // cart/add.js normally answers in well under a second. Anything still
  // outstanding at eight is not slow, it is hung.
  var REQUEST_TIMEOUT_MS = 8000;
  // A heartbeat this far behind schedule means the page was frozen, not busy.
  var FROZEN_GAP_MS = 5000;

  // Set immediately before handing over to checkout. It lives in this closure,
  // so it survives a bfcache restore (same JavaScript context) but is gone
  // after a real page load — which is what stops the reload from looping.
  var leftForCheckout = false;

  function getRootUrl() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  }

  function statusInside(btn) {
    return btn.querySelector('[data-bundle-checkout-status], [data-popup-checkout-status]');
  }

  function setLoading(btn, statusEl, errorEl, loadingLabel) {
    if (btn) {
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      btn.setAttribute('data-loading-since', String(Date.now()));
      btn.classList.add('is-loading');
    }
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }
    if (statusEl) statusEl.textContent = loadingLabel || '';
  }

  function clearLoading(btn, statusEl) {
    if (btn) {
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
      btn.removeAttribute('data-loading-since');
      btn.classList.remove('is-loading');
    }
    if (statusEl) statusEl.textContent = '';
  }

  function showError(errorEl, message, fallback) {
    if (!errorEl) return;
    errorEl.textContent = message || fallback || DEFAULT_ERROR;
    errorEl.hidden = false;
  }

  function reloadForFreshPage() {
    leftForCheckout = false;
    window.location.reload();
  }

  // Last-resort tidy-up: clear any button still marked as loading long after
  // its request should have settled. Reached when a reload did not happen for
  // some reason, so that a customer is never left facing a dead button.
  function sweepStuckButtons(now) {
    var stuck = document.querySelectorAll('[data-loading-since].is-loading');
    Array.prototype.forEach.call(stuck, function (btn) {
      var since = parseInt(btn.getAttribute('data-loading-since'), 10);
      if (isNaN(since) || now - since < REQUEST_TIMEOUT_MS) return;
      clearLoading(btn, statusInside(btn));
    });
  }

  window.addEventListener('pageshow', function (event) {
    if (event.persisted && leftForCheckout) reloadForFreshPage();
  });

  var lastBeat = Date.now();
  setInterval(function () {
    var now = Date.now();
    var gap = now - lastBeat;
    lastBeat = now;

    if (gap > FROZEN_GAP_MS && leftForCheckout) {
      reloadForFreshPage();
      return;
    }

    sweepStuckButtons(now);
  }, HEARTBEAT_MS);

  function checkoutUrl(rootUrl, discount) {
    return rootUrl + 'checkout' + (discount ? '?discount=' + encodeURIComponent(discount) : '');
  }

  // Shopify's cart permalink: a plain navigation that adds the line item
  // server-side. Used when the fetch hangs, because it needs no working XHR.
  function permalinkUrl(rootUrl, variantId, quantity, discount) {
    return rootUrl + 'cart/' + encodeURIComponent(variantId) + ':' + quantity +
      (discount ? '?discount=' + encodeURIComponent(discount) : '');
  }

  function handleBundleProceedToCheckout(variantId, options) {
    options = options || {};
    var btn = options.button || null;
    var statusEl = options.statusEl || null;
    var errorEl = options.errorEl || null;
    var loadingLabel = options.loadingLabel || '';
    var errorLabel = options.errorLabel || DEFAULT_ERROR;
    var discount = (typeof options.discount === 'string') ? options.discount : DEFAULT_DISCOUNT;
    var quantity = options.quantity || 1;

    if (!variantId) {
      showError(errorEl, errorLabel);
      return Promise.reject(new Error('Missing variant id'));
    }

    setLoading(btn, statusEl, errorEl, loadingLabel);

    var rootUrl = getRootUrl();
    var controller = (typeof AbortController === 'function') ? new AbortController() : null;
    var timedOut = false;

    var timeout = setTimeout(function () {
      timedOut = true;
      if (controller) controller.abort();
    }, REQUEST_TIMEOUT_MS);

    return fetch(rootUrl + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: quantity }] }),
      signal: controller ? controller.signal : undefined
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) {
            throw new Error((data && (data.description || data.message)) || errorLabel);
          }
          return data;
        });
      })
      .then(function (data) {
        clearTimeout(timeout);
        leftForCheckout = true;
        window.location.href = checkoutUrl(rootUrl, discount);
        return data;
      })
      .catch(function (err) {
        clearTimeout(timeout);

        // The request never came back. Rather than showing an error, fall back
        // to the permalink: the customer still gets their item and still gets
        // to checkout, without this page needing a working fetch.
        if (timedOut) {
          leftForCheckout = true;
          window.location.href = permalinkUrl(rootUrl, variantId, quantity, discount);
          return;
        }

        clearLoading(btn, statusEl);
        showError(errorEl, err && err.message, errorLabel);
        throw err;
      });
  }

  window.handleBundleProceedToCheckout = handleBundleProceedToCheckout;
})();